import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { MagnifyingGlass, LinkedinLogo, MapPin, Sparkle, Plus, Buildings, Envelope, Phone, IdentificationCard, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ScoreBadge } from "../components/ScoreBadge";

export default function Finder() {
  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState({ specialty: "Internal Medicine", zipcode: "", state: "", radius_miles: 0, practice_type: "independent", limit: 20, enrich_with_ai: true });
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    api.get("/config/specialties").then(r => setSpecialties(r.data.specialties));
  }, []);

  const search = async () => {
    if (!form.zipcode && !form.state) { toast.error("Provide ZIP or State"); return; }
    setLoading(true);
    setSearched(true);
    setSelected(new Set());
    try {
      const r = await api.post("/finder/search", form);
      setResults(r.data.results);
      if (r.data.results.length === 0) toast.warning("No results - try a different ZIP or specialty");
      else toast.success(`Found ${r.data.results.length} physicians`);
    } catch (e) {
      toast.error("Search failed");
    } finally { setLoading(false); }
  };

  const toggle = (npi) => {
    const s = new Set(selected);
    if (s.has(npi)) s.delete(npi); else s.add(npi);
    setSelected(s);
  };

  const addSelected = async () => {
    const toAdd = results.filter(r => selected.has(r.npi));
    if (toAdd.length === 0) { toast.error("Select at least one"); return; }
    const r = await api.post("/finder/add-to-leads", toAdd);
    toast.success(`${r.data.added} added to leads`);
    setSelected(new Set());
  };

  return (
    <div className="p-8" data-testid="finder-page">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">Prospecting</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Physician Finder</h1>
        <p className="text-sm text-[#5C5E66] mt-2 max-w-2xl">Search the public NPI Registry by specialty &amp; location. Optionally enrich top results with AI-generated profile summaries powered by Claude Sonnet 4.5.</p>
      </div>

      {/* Search Card */}
      <div className="bg-white border border-[#E2E2E5] p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Specialty</Label>
            <Select value={form.specialty} onValueChange={(v) => setForm({...form, specialty: v})}>
              <SelectTrigger data-testid="finder-specialty" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {specialties.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Zip Code</Label>
            <Input data-testid="finder-zip" value={form.zipcode} onChange={(e) => setForm({...form, zipcode: e.target.value})} className="rounded-none mt-1 font-mono" placeholder="10001" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Radius (mi)</Label>
            <Select value={String(form.radius_miles)} onValueChange={(v) => setForm({...form, radius_miles: parseInt(v)})}>
              <SelectTrigger data-testid="finder-radius" className="rounded-none mt-1 font-mono"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Exact ZIP</SelectItem>
                <SelectItem value="5">5 mi</SelectItem>
                <SelectItem value="10">10 mi</SelectItem>
                <SelectItem value="25">25 mi</SelectItem>
                <SelectItem value="50">50 mi</SelectItem>
                <SelectItem value="100">100 mi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">State</Label>
            <Input data-testid="finder-state" value={form.state} onChange={(e) => setForm({...form, state: e.target.value.toUpperCase()})} className="rounded-none mt-1 font-mono" placeholder="NY" maxLength={2} />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Type</Label>
            <Select value={form.practice_type} onValueChange={(v) => setForm({...form, practice_type: v})}>
              <SelectTrigger data-testid="finder-ptype" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="independent">Independent</SelectItem>
                <SelectItem value="hospitalist">Hospitalist</SelectItem>
                <SelectItem value="any">All Types</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Result Limit</Label>
            <Input data-testid="finder-limit" type="number" value={form.limit} onChange={(e) => setForm({...form, limit: parseInt(e.target.value || "20")})} className="rounded-none mt-1 font-mono" min={5} max={50} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E2E2E5]">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.enrich_with_ai} onCheckedChange={(v) => setForm({...form, enrich_with_ai: !!v})} data-testid="finder-ai-toggle" className="rounded-none" />
            <Sparkle size={16} weight="fill" className="text-[#FF9900]" />
            <span className="text-sm font-medium">Enrich with AI (LinkedIn-style summary, research experience)</span>
          </label>
          <Button data-testid="finder-search-btn" onClick={search} disabled={loading} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-2">
            <MagnifyingGlass size={16} weight="bold" /> {loading ? "SEARCHING..." : "Search"}
          </Button>
        </div>
      </div>

      {/* Results */}
      {searched && (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy size={14} weight="fill" className="text-[#FF9900]" />
              <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">{results.length} results · ranked by lead score</div>
            </div>
            {selected.size > 0 && (
              <Button data-testid="add-selected-btn" onClick={addSelected} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-1.5">
                <Plus size={14} weight="bold" /> Add {selected.size} to Leads
              </Button>
            )}
          </div>

          {loading ? (
            <div className="p-12 text-center font-mono text-xs uppercase tracking-wider text-[#5C5E66]">QUERYING NPI REGISTRY...</div>
          ) : results.length === 0 ? (
            <div className="bg-white border border-[#E2E2E5] p-12 text-center text-[#5C5E66]">
              <div className="text-sm">No physicians found. Try expanding criteria.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((p, idx) => (
                <div key={p.npi || idx} className={`bg-white border p-4 ${selected.has(p.npi) ? "border-[#006CFF] border-2" : "border-[#E2E2E5]"}`} data-testid={`finder-result-${idx}`}>
                  <div className="flex items-start gap-4">
                    <Checkbox checked={selected.has(p.npi)} onCheckedChange={() => toggle(p.npi)} className="rounded-none mt-1" data-testid={`finder-select-${idx}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-[#5C5E66]">#{idx + 1}</span>
                            <div className="font-display font-bold text-lg">{p.full_name}</div>
                            <ScoreBadge score={p.lead_score} testid={`finder-score-${idx}`} />
                          </div>
                          <div className="text-sm text-[#5C5E66] mt-0.5">{p.specialty}</div>
                        </div>
                        <span className="px-2 py-0.5 bg-[#F0F0F2] text-[10px] font-mono uppercase tracking-wider whitespace-nowrap">{p.practice_type}</span>
                      </div>
                      {p.distance_miles !== undefined && (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[#006CFF] mt-1">{p.distance_miles} miles away</div>
                      )}

                      {/* Clinic */}
                      {p.practice_name && (
                        <div className="mt-2 flex items-center gap-2 text-sm">
                          <Buildings size={14} className="text-[#FF9900]" />
                          <span className="font-medium">Clinic:</span>
                          <span className="text-[#0A0B0F]">{p.practice_name}</span>
                        </div>
                      )}

                      {/* Contact info grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 mt-3 text-xs">
                        {p.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone size={12} className="text-[#5C5E66]" />
                            <a href={`tel:${p.phone}`} className="font-mono hover:text-[#006CFF]">{p.phone}</a>
                          </div>
                        )}
                        {p.email && (
                          <div className="flex items-center gap-1.5">
                            <Envelope size={12} className="text-[#5C5E66]" />
                            <a href={`mailto:${p.email}`} className="hover:text-[#006CFF]">{p.email}</a>
                          </div>
                        )}
                        {(p.city || p.state) && (
                          <div className="flex items-center gap-1.5 text-[#5C5E66]">
                            <MapPin size={12} />
                            {[p.address, p.city, p.state, p.zipcode].filter(Boolean).join(", ")}
                          </div>
                        )}
                        {p.linkedin_url && (
                          <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#006CFF] hover:underline">
                            <LinkedinLogo size={12} />Search LinkedIn
                          </a>
                        )}
                        {p.npi && (
                          <div className="flex items-center gap-1.5 text-[#5C5E66]">
                            <IdentificationCard size={12} />
                            <span className="font-mono">NPI · {p.npi}</span>
                          </div>
                        )}
                      </div>

                      {p.ai_summary && (
                        <div className="mt-3 pt-3 border-t border-[#E2E2E5]">
                          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#FF9900] mb-1.5"><Sparkle size={10} weight="fill" /> AI Summary</div>
                          <p className="text-xs leading-relaxed">{p.ai_summary}</p>
                          {p.research_experience && <p className="text-xs leading-relaxed mt-1.5 text-[#5C5E66]">{p.research_experience}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
